import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const LINK_ID = 'canonical-url';

/**
 * CanonicalUrl component - manages the <link rel="canonical"> tag.
 *
 * On initial mount, hydrates from the server-rendered tag.
 * On route changes, updates the href to the current clean URL (no query params).
 *
 * Props:
 *   baseUrl - (optional) overrides window.location.origin for building the canonical
 *   href    - (optional) explicit canonical URL; if omitted, uses baseUrl + pathname
 */
export function CanonicalUrl({ baseUrl, href }) {
  const location = useLocation();
  const linkRef = useRef(null);

  useEffect(() => {
    let link = document.getElementById(LINK_ID);
    if (!link) {
      // Find existing server-rendered canonical
      link = document.querySelector('link[rel="canonical"]');
      if (link) {
        link.id = LINK_ID;
      } else {
        link = document.createElement('link');
        link.id = LINK_ID;
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
    }
    linkRef.current = link;

    const origin = baseUrl || window.location.origin;
    // Clean pathname only — never include search/hash
    const pathname = location.pathname.replace(/\/+$/, '') || '/';
    const canonicalHref = href || `${String(origin).replace(/\/$/, '')}${pathname === '/' ? '/' : pathname}`;
    link.href = canonicalHref;
  }, [location.pathname, baseUrl, href]);

  return null;
}
